import './clickable_card.css'

import ArrowRightOutlined from '@ant-design/icons/ArrowRightOutlined'
import BorderInnerOutlined from '@ant-design/icons/BorderInnerOutlined'
import DownSquareOutlined from '@ant-design/icons/DownSquareOutlined'
import EditOutlined from '@ant-design/icons/EditOutlined'
import PushpinOutlined from '@ant-design/icons/PushpinOutlined'
import SearchOutlined from '@ant-design/icons/SearchOutlined'
import { Link } from '@tanstack/react-router'
import {
  Card,
  Input,
  InputRef,
  List,
  Popover,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import {
  BlankIndicationClassificationTag,
  IndicationClassificationConfigContext,
  IndicationClassificationTag,
  IndicationItem,
  useUpdateIndicationContext,
} from 'features/indicationTriage'
import isNil from 'lodash/isNil'
import reduce from 'lodash/reduce'
import React, {
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import styled from 'styled-components'
import { useGeckoTheme } from 'styles/GeckoTheme'

export type IndicationTriageHighlightType = {
  id: string
  highlight: HighlightType
}

export type HighlightOptions = {
  showHighlight?: boolean
  showZoom?: boolean
}

export type HighlightType = 'full-rez' | 'outline'

interface IndicationTriageListRowProps {
  item: IndicationItem
  highlight: HighlightType | null
  regionUpdateInProgress?: boolean
  selectedComponentId: string | undefined
  setRegionUpdateInProgress?: (id: string, inProgress: boolean) => void
  setHighlight: (id: string, type: HighlightType | null) => void
  highlightOptions?: HighlightOptions
  classificationTooltipPrefix?: string
  onCardClick?: (id: string) => void
  allowCreateOrChangeRegion?: boolean
}

const { Text } = Typography

const IndicationTriageListRowRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
`

const IndicationTriageListRowContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 1rem;
`

const IndicationLocationControlsIconStyle: React.CSSProperties = {
  fontSize: '15px',
}
const ControlItemContainer = styled.div`
  display: flex;
  gap: 1rem;
  align-items: left;
  justify-content: left;
`
type IndicationRowControlsProps = Pick<
  IndicationTriageListRowProps,
  | 'item'
  | 'highlight'
  | 'highlightOptions'
  | 'setHighlight'
  | 'regionUpdateInProgress'
  | 'setRegionUpdateInProgress'
> & {
  setTitleUpdateInProgress: Dispatch<SetStateAction<boolean>>
  titleUpdateInProgress: boolean
  allowCreateOrChangeRegion?: boolean
}
type ControlItem = {
  label: string
  icon: (style: React.CSSProperties) => ReactNode
  onChange: (checked: boolean) => void
  checked: boolean
  disabled: boolean
  allowCreateOrChangeRegion?: boolean
}

function useIndicationRowControlItems(
  props: IndicationRowControlsProps
): ControlItem[] {
  const {
    titleUpdateInProgress,
    setTitleUpdateInProgress,
    highlight,
    item,
    regionUpdateInProgress,
    setRegionUpdateInProgress,
    highlightOptions,
    setHighlight,
    allowCreateOrChangeRegion,
  } = props
  return useMemo<ControlItem[]>(() => {
    return [
      {
        label: 'Edit Title',
        icon: (style: React.CSSProperties) => <EditOutlined style={style} />,
        onChange: () => setTitleUpdateInProgress((prev) => !prev),
        checked: titleUpdateInProgress,
        disabled: !setTitleUpdateInProgress,
      },
      {
        label: 'Locate',
        icon: (style: React.CSSProperties) => <PushpinOutlined style={style} />,
        onChange: (checked: boolean) =>
          setHighlight(item.id, checked ? 'outline' : null),
        checked: highlight === 'outline',
        disabled: !item.selection || !highlightOptions?.showHighlight,
      },
      {
        label: 'Zoom',
        icon: (style: React.CSSProperties) => <SearchOutlined style={style} />,
        onChange: (checked: boolean) =>
          setHighlight(item.id, checked ? 'full-rez' : null),
        checked: highlight === 'full-rez',
        disabled: !item.selection || !highlightOptions?.showZoom,
      },
      {
        label: 'Edit region',
        icon: (style: React.CSSProperties) => (
          <BorderInnerOutlined style={style} />
        ),
        onChange: (checked: boolean) =>
          setRegionUpdateInProgress?.(item.id, checked),
        checked: !!regionUpdateInProgress,
        // if the config has viewOnly, we don't show the edit region control
        disabled: allowCreateOrChangeRegion === false,
      },
    ].filter((controlItem) => !controlItem.disabled)
  }, [
    highlight,
    highlightOptions?.showHighlight,
    highlightOptions?.showZoom,
    item.id,
    item.selection,
    regionUpdateInProgress,
    setHighlight,
    setRegionUpdateInProgress,
    setTitleUpdateInProgress,
    titleUpdateInProgress,
    allowCreateOrChangeRegion,
  ])
}

const RawIndicationRowControls: React.FC<{ controlItems: ControlItem[] }> = ({
  controlItems,
}) => {
  const renderControlItem = useCallback((item: ControlItem) => {
    return (
      <List.Item style={{ padding: '2px' }}>
        <Tag.CheckableTag
          checked={item.checked}
          onChange={item.onChange}
          onClick={(e) => e.stopPropagation()}>
          <ControlItemContainer>
            <>{item.icon(IndicationLocationControlsIconStyle)}</>
            <Text>{item.label}</Text>
          </ControlItemContainer>
        </Tag.CheckableTag>
      </List.Item>
    )
  }, [])

  return (
    <List
      dataSource={controlItems}
      renderItem={renderControlItem}
      itemLayout='vertical'
      bordered={false}
      size='small'
      split={false}
    />
  )
}
const IndicationRowControls = React.memo(RawIndicationRowControls)

const ItemCardStyle: React.CSSProperties = {
  width: '100%',
}
const ItemCardBodyStyle: React.CSSProperties = {
  paddingLeft: '20px',
  paddingRight: '20px',
  paddingTop: '10px',
  paddingBottom: '10px',
}

const RawIndicationTriageListRow: FC<IndicationTriageListRowProps> = ({
  item,
  classificationTooltipPrefix,
  selectedComponentId,
  highlight,
  setHighlight,
  highlightOptions,
  regionUpdateInProgress,
  setRegionUpdateInProgress,
  onCardClick,
  allowCreateOrChangeRegion,
}) => {
  const [editingTitle, setEditingTitle] = useState<string | null>(null)
  const [titleUpdateInProgress, setTitleUpdateInProgress] =
    useState<boolean>(false)
  const titleInputRef = useRef<InputRef | null>(null)
  useEffect(() => {
    if (titleInputRef.current && titleUpdateInProgress) {
      titleInputRef.current.focus()
    }
  }, [titleUpdateInProgress])

  const { updateOrAddIndication } = useUpdateIndicationContext()
  const handleTitleUpdate = useCallback(() => {
    setTitleUpdateInProgress(false)
    if (!updateOrAddIndication || isNil(editingTitle)) {
      return
    }
    updateOrAddIndication({
      ...item,
      title: editingTitle ?? '',
    })
    setEditingTitle(null)
  }, [editingTitle, item, updateOrAddIndication])

  const { config: classificationConfig } = useContext(
    IndicationClassificationConfigContext
  )

  const cardBodyStyle = useMemo(() => ({ body: ItemCardBodyStyle }), [])
  const controlItems = useIndicationRowControlItems({
    item: item,
    highlight: highlight,
    setHighlight: setHighlight,
    highlightOptions: highlightOptions,
    regionUpdateInProgress: regionUpdateInProgress,
    setRegionUpdateInProgress: setRegionUpdateInProgress,
    titleUpdateInProgress: titleUpdateInProgress,
    setTitleUpdateInProgress: setTitleUpdateInProgress,
    allowCreateOrChangeRegion: allowCreateOrChangeRegion,
  })
  const anyChecked = useMemo(
    () =>
      reduce(
        controlItems.map((item) => item.checked),
        (prev, curr) => prev || curr,
        false
      ),
    [controlItems]
  )

  const { theme } = useGeckoTheme()

  return (
    <Card
      data-clickable-card={onCardClick ? 'true' : undefined}
      style={ItemCardStyle}
      styles={cardBodyStyle}
      key={item.id}
      onClick={
        onCardClick
          ? (e) => {
              e.stopPropagation()
              onCardClick(item.id)
            }
          : undefined
      }>
      <IndicationTriageListRowContainer>
        <IndicationTriageListRowRow>
          {updateOrAddIndication && titleUpdateInProgress ? (
            <Input
              ref={titleInputRef}
              width={12}
              value={isNil(editingTitle) ? item.title : editingTitle}
              onChange={(e) => {
                setEditingTitle(e.target.value)
              }}
              onClick={(e) => e.stopPropagation()}
              onBlur={handleTitleUpdate}
              onPressEnter={handleTitleUpdate}
            />
          ) : (
            <Text style={{ width: '100%' }}>{item.title}</Text>
          )}
          {classificationConfig.showTags !== false ? (
            <>
              <Text>|</Text>
              {item.classificationInfo ? (
                <IndicationClassificationTag
                  value={item.classificationInfo.refVal}
                  classification={item.classificationInfo.classification}
                  tooltipPrefix={classificationTooltipPrefix}
                />
              ) : (
                <BlankIndicationClassificationTag />
              )}
            </>
          ) : (
            <></>
          )}
          {selectedComponentId === item.componentId &&
            !!controlItems.length && (
              <>
                <Text>|</Text>
                <Popover
                  content={
                    <IndicationRowControls controlItems={controlItems} />
                  }
                  placement='bottom'
                  style={{ display: 'none' }}>
                  <Tag.CheckableTag
                    checked={anyChecked}
                    style={{
                      cursor: 'pointer',
                      border: 'none',
                      position: 'relative',
                      top: '2px',
                      left: '2px',
                    }}
                    onClick={(e) => e.stopPropagation()}>
                    <DownSquareOutlined
                      style={{
                        fontSize: '20px',
                        color: theme.forecast.secondaryText,
                      }}
                    />
                  </Tag.CheckableTag>
                </Popover>
              </>
            )}
        </IndicationTriageListRowRow>
        {selectedComponentId !== item.componentId && item.componentId && (
          <IndicationTriageListRowRow>
            <Link
              to='/details/$entryID'
              params={{ entryID: encodeURIComponent(item.componentId) }}
              search={{
                extension: 'IndicationTriage',
                indication: encodeURIComponent(item.id),
              }}>
              <Tooltip placement='left' title={`Redirect to Component`}>
                <Tag.CheckableTag checked={false}>
                  <Text strong>Go to component</Text>
                  <ArrowRightOutlined />
                </Tag.CheckableTag>
              </Tooltip>
            </Link>
          </IndicationTriageListRowRow>
        )}
      </IndicationTriageListRowContainer>
    </Card>
  )
}

export const IndicationTriageListRow = React.memo(RawIndicationTriageListRow)
